import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, MapPin, MessageCircle } from "lucide-react";
import "./ConsultationIntake.css";

const painOptions = ["Neck pain", "Lower back pain", "Upper back or shoulders", "Posture or mobility", "Something else"];
const routineOptions = ["Desk work", "Long commute", "Standing all day", "Mixed routine", "Something else"];
const careOptions = ["In-clinic", "Online", "Not sure yet"];

const ease = [0.22, 1, 0.36, 1];

export function ConsultationIntake({ whatsappBaseUrl, clinicAddress, directionsUrl }) {
  const [form, setForm] = useState({ name: "", painLocation: "", routine: "", carePreference: "", notes: "" });
  const [error, setError] = useState("");

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const submitIntake = (event) => {
    event.preventDefault();
    if (!form.painLocation || !form.routine || !form.carePreference) {
      setError("Please choose your pain area, daily routine, and care preference before continuing.");
      return;
    }

    const message = [
      "Assalam-o-Alaikum Zuhab, I would like to book a physiotherapy consultation.",
      form.name ? `Name: ${form.name}` : "Name: Not provided",
      `Pain location: ${form.painLocation}`,
      `Daily routine: ${form.routine}`,
      `Care preference: ${form.carePreference}`,
      form.notes ? `Notes: ${form.notes}` : "Notes: None",
    ].join("\n");

    window.open(`${whatsappBaseUrl}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="intake" className="landing-section landing-intake-section" data-testid="consultation-intake-section">
      <div className="landing-container landing-intake-grid">
        <motion.div
          className="landing-intake-intro"
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.72, ease }}
        >
          <p className="landing-kicker">Consultation intake</p>
          <h2>Tell Zuhab what your body is dealing with.</h2>
          <p className="landing-section-text">Answer three quick questions before booking. Your responses open WhatsApp as a clear, structured message, so your consultation starts with useful context.</p>
          <div className="landing-location-card" data-testid="clinic-location-card">
            <div className="landing-location-icon"><MapPin size={22} /></div>
            <div>
              <span>In-clinic location</span>
              <strong>{clinicAddress}</strong>
              <a href={directionsUrl} target="_blank" rel="noreferrer" data-testid="clinic-directions-link">Get directions <ArrowUpRight size={15} /></a>
            </div>
          </div>
          <div className="landing-next-list">
            {["Share what hurts", "Choose clinic or online care", "Continue directly in WhatsApp"].map((item) => <span key={item}><CheckCircle2 size={16} />{item}</span>)}
          </div>
        </motion.div>

        <motion.form
          className="landing-intake-form"
          onSubmit={submitIntake}
          initial={{ opacity: 0, y: 38 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.18 }}
          transition={{ duration: 0.72, delay: 0.1, ease }}
          data-testid="consultation-intake-form"
        >
          <label>
            <span>Your name <em>optional</em></span>
            <input value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder="Enter your name" data-testid="intake-name-input" />
          </label>
          <div className="landing-intake-row">
            <label>
              <span>Where is the pain?</span>
              <select value={form.painLocation} onChange={(event) => updateField("painLocation", event.target.value)} data-testid="intake-pain-select">
                <option value="">Select pain area</option>
                {painOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <label>
              <span>Your daily routine</span>
              <select value={form.routine} onChange={(event) => updateField("routine", event.target.value)} data-testid="intake-routine-select">
                <option value="">Select routine</option>
                {routineOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
          </div>
          <fieldset>
            <legend>How would you prefer care?</legend>
            <div className="landing-care-choice-grid">
              {careOptions.map((option) => (
                <label className={`landing-care-choice ${form.carePreference === option ? "is-selected" : ""}`} key={option}>
                  <input
                    type="radio"
                    name="care-preference"
                    value={option}
                    checked={form.carePreference === option}
                    onChange={(event) => updateField("carePreference", event.target.value)}
                    data-testid={`intake-care-${option.toLowerCase().replaceAll(" ", "-")}`}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label>
            <span>Anything Zuhab should know? <em>optional</em></span>
            <textarea value={form.notes} onChange={(event) => updateField("notes", event.target.value)} placeholder="For example: pain gets worse after sitting for two hours" rows={4} data-testid="intake-notes-input" />
          </label>
          {error && <p className="landing-intake-error" role="alert" data-testid="intake-error">{error}</p>}
          <button className="landing-button landing-button-primary landing-intake-submit" type="submit" data-testid="intake-submit-button">
            Continue on WhatsApp <MessageCircle size={18} />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
